import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import PageShell, { Section } from "@/components/site/PageShell";
import { Cards, Note, Prose } from "@/components/site/ui";
import { COMMON, COTISATION, COTISATION_FAQ, TIERS } from "@/content/cotisation";
import { BASE, CRITERIA } from "@/content/pricing";

// Recalcule l'équivalent à la carte depuis la grille : si les tarifs changent,
// la page suit au lieu d'afficher une économie périmée.
//
// La correspondance se fait sur le libellé EXACT de l'option. Une recherche
// approximative renverrait « Non incluse » avant « Incluse » et sous-évaluerait
// l'équivalent de 44 € sans que rien ne le signale.
function delta(critere, libelle) {
  const option = CRITERIA.find(c => c.key === critere)?.options.find(o => o.label === libelle);
  if (!option) {
    // Le libellé a changé dans la grille : mieux vaut un échec visible qu'un
    // prix faux affiché sans bruit.
    throw new Error(`Option tarifaire introuvable : ${critere} / ${libelle}`);
  }
  return option.delta;
}

function equivalent(lignes) {
  return lignes.reduce((total, [label, qty]) => {
    if (label.includes("compétition")) return total + delta("competition", "Incluse") * qty;
    let unit = BASE;
    if (label.includes("1 h 30")) unit += delta("duree", "1 h 30");
    if (label.includes("personnalisée")) unit += delta("module", "Personnalisé");
    return total + unit * qty;
  }, 0);
}

export default function Cotisation() {
  return (
    <PageShell eyebrow={COTISATION.eyebrow} title={COTISATION.title} lead={COTISATION.lead}
      actions={<>
        <Link to="/auth" className="button button-dark" data-testid="cotisation-start">Créer mon compte<ArrowRight size={13} /></Link>
        <Link to="/tarifs" className="button button-outline">Tarif à la séance</Link>
      </>}>

      <Section><Note kind="info">{COTISATION.notice}</Note></Section>

      <Section>
        <div className="card-grid">
          {TIERS.map(t => {
            const eq = equivalent(t.equivalent);
            const pct = Math.round((1 - t.price / eq) * 100);
            return (
              <div key={t.key} className={`plan${t.highlight ? " is-highlight" : ""}`} data-testid={`tier-${t.key}`}>
                <h3>{t.name}</h3>
                <div className="plan-price">{t.price} €<small>{t.unit}</small></div>
                <p className="plan-text">{t.text}</p>
                <div className="tier-saving">
                  Les mêmes séances à la carte : <strong>{eq} €</strong> — soit <strong>{pct} % d'économie</strong>.
                </div>
                <ul>{t.features.map(f => <li key={f}><Check size={13} />{f}</li>)}</ul>
                <Link to="/auth" className={`button ${t.highlight ? "button-dark" : "button-outline"}`}>
                  Choisir {t.name}<ArrowRight size={13} />
                </Link>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Valable pour tous les paliers"><Cards items={COMMON} cols={2} /></Section>

      <Section title="Et si je préfère payer à la séance ?">
        <Prose>
          <p>
            C'est possible, et parfois plus sensé : si vous ne pouvez pas tenir un rythme
            régulier, l'abonnement vous ferait payer des séances que vous ne prendriez pas.
            Le <Link to="/tarifs">tarif à la carte</Link> va de {BASE} à 200 € la séance.
          </p>
          <p>
            L'abonnement devient intéressant à partir de deux séances par mois. En dessous,
            restez à la carte.
          </p>
        </Prose>
      </Section>

      <Section title="Questions fréquentes">
        <div className="faq-list" style={{ maxWidth: 720 }}>
          {COTISATION_FAQ.map(f => (
            <details key={f.q}><summary><span>{f.q}</span><Minus size={14} /></summary><p>{f.a}</p></details>
          ))}
        </div>
      </Section>
    </PageShell>
  );
}
